import React from "react";
import "./GiftAnyAmount.scss";
import useModalWindowControls from "../../../Hooks/useModal";
import GiftCardDetailsModal from "../GiftCardDetailsModal";

export interface UniversalGiftData {
  type: "All";
  price: string;
  title: string;
  description: string;
}

const MIN_AMOUNT = 25;
const MAX_AMOUNT = 1000;
const PRESET_AMOUNTS = [50, 75, 100, 150, 200];

const GiftAnyAmount = () => {
  const sendGiftModal = useModalWindowControls();
  const [amount, setAmount] = React.useState("100");
  const [error, setError] = React.useState("");

  const description =
    "Give your loved one the flexibility to choose any Nurture by NAPS service, class, course, or event — and even put it toward their first membership payment. They can use the full amount or part of it and save the rest for later. To gift a full membership plan directly, use the membership options above.";

  const parsedAmount = Number(amount);

  const openGiftModal = () => {
    if (!parsedAmount || parsedAmount < MIN_AMOUNT || parsedAmount > MAX_AMOUNT) {
      setError(`Enter an amount between $${MIN_AMOUNT} and $${MAX_AMOUNT}.`);
      return;
    }

    setError("");
    sendGiftModal.openModal({
      type: "All",
      price: String(parsedAmount),
      title: `$${parsedAmount} Gift Card`,
      description,
    });
  };

  const handlePresetClick = (preset: number) => {
    setAmount(String(preset));
    setError("");
  };

  return (
    <section className="GiftAnyAmount">
      <GiftCardDetailsModal
        isVisible={sendGiftModal.isVisible}
        closeModal={sendGiftModal.closeModal}
        openModalData={sendGiftModal.openModalData}
      />

      <div className="GiftAnyAmount__card">
        <div className="GiftAnyAmount__visual">
          <div className="GiftAnyAmount__giftCardMock" aria-hidden="true">
            <div className="GiftAnyAmount__giftCardMockLogo">NAPS</div>
            <div className="GiftAnyAmount__giftCardMockLabel">Gift Card</div>
            <div className="GiftAnyAmount__giftCardMockAmount">
              ${Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : "—"}
            </div>
            <div className="GiftAnyAmount__giftCardMockFooter">Use on any service</div>
          </div>
          <div className="GiftAnyAmount__sparkle GiftAnyAmount__sparkle--one" aria-hidden="true" />
          <div className="GiftAnyAmount__sparkle GiftAnyAmount__sparkle--two" aria-hidden="true" />
        </div>

        <div className="GiftAnyAmount__content">
          <span className="GiftAnyAmount__eyebrow">Flexible gifting</span>
          <h2 className="GiftAnyAmount__title">
            Gift <span className="GiftAnyAmount__titleAccent">any amount</span>
          </h2>
          <p className="GiftAnyAmount__description">{description}</p>

          <ul className="GiftAnyAmount__benefits">
            <li>Any consult, class, course, or event</li>
            <li>Can cover the first membership charge</li>
            <li>Use part now, save the rest</li>
            <li>Perfect when you&apos;re not sure what they need</li>
          </ul>
        </div>

        <div className="GiftAnyAmount__form">
          <div className="GiftAnyAmount__formTitle">Choose an amount</div>

          <div className="GiftAnyAmount__presets">
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`GiftAnyAmount__preset${
                  parsedAmount === preset ? " GiftAnyAmount__preset--active" : ""
                }`}
                onClick={() => handlePresetClick(preset)}
              >
                ${preset}
              </button>
            ))}
          </div>

          <div className="GiftAnyAmount__customDivider">
            <span>or custom amount</span>
          </div>

          <div className="GiftAnyAmount__customField">
            <div
              className={`GiftAnyAmount__inputWrap${
                error ? " GiftAnyAmount__inputWrap--error" : ""
              }`}
            >
              <label className="GiftAnyAmount__customLabel" htmlFor="gift-any-amount">
                Custom amount
              </label>
              <div className="GiftAnyAmount__inputInner">
                <span className="GiftAnyAmount__currency" aria-hidden="true">
                  $
                </span>
                <input
                  id="gift-any-amount"
                  className="GiftAnyAmount__input"
                  type="number"
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  step={1}
                  inputMode="numeric"
                  placeholder="100"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      openGiftModal();
                    }
                  }}
                />
                <span className="GiftAnyAmount__suffix">USD</span>
              </div>
            </div>
          </div>

          <div className="GiftAnyAmount__hint">
            ${MIN_AMOUNT} minimum · ${MAX_AMOUNT} maximum
          </div>

          {error && <p className="GiftAnyAmount__error">{error}</p>}

          <button type="button" className="GiftAnyAmount__cta primary-blue-btn" onClick={openGiftModal}>
            Continue with ${Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : "—"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default GiftAnyAmount;
